import React, { useState, useRef, useCallback } from 'react';
import { FileSpreadsheet, Download, Loader, Upload, CheckCircle, XCircle, AlertTriangle, Info, Play, ChevronDown, ChevronRight, Minus } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { TABLE_CONFIG, generateImportTemplate } from '../../utils/importTemplateBuilder';
import { validateImportFile } from '../../utils/importValidator';
import { executeImport } from '../../utils/importEngine';

const ImportTemplatePage = () => {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null);

  // Validation state
  const [validating, setValidating] = useState(false);
  const [valProgress, setValProgress] = useState(null);
  const [valResult, setValResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Import execution state
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [expandedErrors, setExpandedErrors] = useState({});

  const handleGenerate = async () => {
    setGenerating(true);
    setGenProgress({ step: 0, total: 4, message: 'Starting...' });

    await generateImportTemplate({
      selectedTables: TABLE_CONFIG,
      onProgress: setGenProgress,
      notify
    });

    setGenerating(false);
    setGenProgress(null);
  };

  const handleValidate = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xlsm')) {
      notify.error('Please upload an .xlsx or .xlsm file');
      return;
    }

    setValidating(true);
    setValResult(null);
    setImportResult(null);
    setImportProgress(null);
    setValProgress({ step: 0, total: 7, message: 'Starting...' });

    try {
      const result = await validateImportFile({
        file,
        onProgress: setValProgress
      });

      setValResult(result);

      if (result.structureFailure) {
        notify.error(`Column structure invalid — ${result.totalErrors} column${result.totalErrors !== 1 ? 's' : ''} out of place. Fix columns before data can be validated.`);
      } else if (result.totalErrors > 0) {
        notify.warning(`Validation found ${result.totalErrors} error${result.totalErrors !== 1 ? 's' : ''}`);
      } else {
        const parts = [];
        if (result.totalInserts > 0) parts.push(`${result.totalInserts} insert${result.totalInserts !== 1 ? 's' : ''}`);
        if (result.totalUpdates > 0) parts.push(`${result.totalUpdates} update${result.totalUpdates !== 1 ? 's' : ''}`);
        if (result.totalSkips > 0) parts.push(`${result.totalSkips} skip${result.totalSkips !== 1 ? 's' : ''}`);
        notify.success(`Validation passed — ${parts.length > 0 ? parts.join(', ') : 'no records to process'}`);
      }
    } catch (err) {
      notify.error('Validation failed: ' + err.message);
    } finally {
      setValidating(false);
      setValProgress(null);
    }
  }, [notify]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    handleValidate(file);
  }, [handleValidate]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    handleValidate(file);
    e.target.value = '';
  };

  const handleDownloadAnnotated = () => {
    if (!valResult?.annotatedBuffer) return;
    const blob = new Blob([valResult.annotatedBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PMD_Validation_Report_${timestamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!valResult || valResult.totalErrors > 0) return;
    setImporting(true);
    setImportResult(null);
    setImportProgress(null);
    setExpandedErrors({});

    try {
      const result = await executeImport({
        valResult,
        onProgress: setImportProgress,
        user: currentUser?.user_Name || currentUser?.full_Name || 'Import'
      });

      setImportResult(result);

      if (result.success) {
        notify.success(`Import complete — ${result.totalInserted} inserted, ${result.totalUpdated} updated`);
      } else {
        notify.warning(`Import complete with ${result.totalFailed} failure${result.totalFailed !== 1 ? 's' : ''}`);
      }
    } catch (err) {
      notify.error('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const toggleErrorExpand = (tabName) => {
    setExpandedErrors(prev => ({ ...prev, [tabName]: !prev[tabName] }));
  };

  const genPercent = genProgress ? Math.round((genProgress.step / genProgress.total) * 100) : 0;
  const valPercent = valProgress ? Math.round((valProgress.step / valProgress.total) * 100) : 0;

  // Determine overall result status
  const getResultClass = () => {
    if (!valResult) return '';
    if (valResult.totalErrors > 0) return 'fail';
    return 'pass';
  };

  return (
    <div className="dev-module import-template-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <FileSpreadsheet size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Import Template
          </h1>
          <span className="dev-module-meta">{TABLE_CONFIG.length} tables</span>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="import-steps-layout">
          {/* Step 1 */}
          <div className="import-step-panel">
            <div className="import-step-header">
              <span className="import-step-number">1</span>
              <h3>Generate Template</h3>
            </div>
            <p className="import-step-desc">Generate Excel workbook for bulk data import.</p>
            <button
              className="import-generate-btn"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <>
                  <Loader size={16} className="spinning" />
                  Generating...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Generate Template
                </>
              )}
            </button>

            {genProgress && (
              <div className="import-progress">
                <div className="import-progress-bar">
                  <div className="import-progress-fill" style={{ width: `${genPercent}%` }} />
                </div>
                <span className="import-progress-text">
                  {genProgress.message} ({genProgress.step}/{genProgress.total})
                </span>
              </div>
            )}
          </div>

          {/* Step 2 */}
          <div className="import-step-panel">
            <div className="import-step-header">
              <span className="import-step-number">2</span>
              <h3>Validation</h3>
            </div>
            <p className="import-step-desc">Drag and drop import file to check for validation.</p>

            <div
              className={`import-dropzone ${dragOver ? 'drag-over' : ''} ${validating ? 'validating' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !validating && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {validating ? (
                <>
                  <Loader size={28} className="spinning" />
                  <span>Validating...</span>
                </>
              ) : (
                <>
                  <Upload size={28} />
                  <span>Drop .xlsx file here or click to browse</span>
                </>
              )}
            </div>

            {valProgress && (
              <div className="import-progress">
                <div className="import-progress-bar">
                  <div className="import-progress-fill" style={{ width: `${valPercent}%` }} />
                </div>
                <span className="import-progress-text">
                  {valProgress.message} ({valProgress.step}/{valProgress.total})
                </span>
              </div>
            )}

            {/* Validation Results */}
            {valResult && (
              <div className={`import-val-result ${getResultClass()}`}>
                {/* Summary line */}
                <div className="import-val-summary">
                  {valResult.totalErrors > 0 ? (
                    <>
                      <XCircle size={20} />
                      <span>
                        {valResult.totalErrors} error{valResult.totalErrors !== 1 ? 's' : ''}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      <span>Validation passed</span>
                    </>
                  )}
                </div>

                {/* Errors by tab */}
                {valResult.totalErrors > 0 && (
                  <div className="import-val-section">
                    <div className="import-val-section-label error">Errors</div>
                    <div className="import-val-tabs">
                      {Object.entries(valResult.errorsByTab).map(([tabName, errors]) => (
                        <div key={tabName} className="import-val-tab-row error">
                          <XCircle size={14} />
                          <span className="import-val-tab-name">{tabName}</span>
                          <span className="import-val-tab-count">{errors.length}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Record Actions by tab */}
                {valResult.recordsByTab && Object.keys(valResult.recordsByTab).length > 0 && (
                  <div className="import-val-section">
                    <div className="import-val-section-label records">Record Actions</div>
                    <div className="import-val-tabs">
                      {Object.entries(valResult.recordsByTab).map(([tabName, records]) => {
                        const insertCount = records.filter(r => r.status === 'insert').length;
                        const updateCount = records.filter(r => r.status === 'update').length;
                        const skipCount = records.filter(r => r.status === 'skip').length;
                        return (
                          <div key={tabName} className="import-val-tab-row records">
                            <Info size={14} />
                            <span className="import-val-tab-name">{tabName}</span>
                            <span className="import-val-tab-detail">
                              {insertCount > 0 && <span className="import-val-badge insert">{insertCount} insert</span>}
                              {updateCount > 0 && <span className="import-val-badge update">{updateCount} update</span>}
                              {skipCount > 0 && <span className="import-val-badge skip">{skipCount} skip</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="import-val-actions">
                  {(valResult.totalErrors > 0 || (valResult.recordsByTab && Object.keys(valResult.recordsByTab).length > 0)) && (
                    <button className="import-generate-btn" onClick={handleDownloadAnnotated}>
                      <Download size={16} />
                      Download Annotated Report
                    </button>
                  )}
                  {valResult.totalErrors === 0 && (valResult.totalInserts > 0 || valResult.totalUpdates > 0) && (
                    <button
                      className="import-execute-btn"
                      onClick={handleImport}
                      disabled={importing}
                    >
                      {importing ? (
                        <><Loader size={16} className="spinning" /> Importing...</>
                      ) : (
                        <><Play size={16} /> Import {valResult.totalInserts + valResult.totalUpdates} Record{valResult.totalInserts + valResult.totalUpdates !== 1 ? 's' : ''}</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Step 3 — Import Execution (appears when importing or import complete) */}
          {(importing || importResult) && (
            <div className="import-step-panel">
              <div className="import-step-header">
                <span className="import-step-number">3</span>
                <h3>Import Execution</h3>
              </div>

              {/* Progress during import */}
              {importing && importProgress && (
                <div className="import-execution-progress">
                  <div className="import-progress-bar">
                    <div
                      className="import-progress-fill"
                      style={{ width: `${Math.round(((importProgress.tabIndex + 1) / importProgress.tabCount) * 100)}%` }}
                    />
                  </div>
                  <span className="import-progress-text">
                    Processing {importProgress.tabName} ({importProgress.tabIndex + 1}/{importProgress.tabCount})
                  </span>
                </div>
              )}

              {/* Per-tab status rows */}
              {importResult && (
                <div className="import-tab-status">
                  {TABLE_CONFIG.map(({ tabName }) => {
                    const tabRes = importResult.resultsByTab[tabName];
                    if (!tabRes) return null;

                    const hasRecords = tabRes.inserted + tabRes.updated + tabRes.failed > 0;
                    const hasFails = tabRes.failed > 0;
                    const isExpanded = expandedErrors[tabName];

                    return (
                      <div key={tabName} className="import-tab-result-group">
                        <div
                          className={`import-tab-row ${hasFails ? 'has-errors' : ''}`}
                          onClick={() => hasFails && toggleErrorExpand(tabName)}
                          style={{ cursor: hasFails ? 'pointer' : 'default' }}
                        >
                          <span className="import-tab-icon">
                            {!hasRecords ? <Minus size={16} className="icon-skip" /> :
                              hasFails ? <XCircle size={16} className="icon-fail" /> :
                              <CheckCircle size={16} className="icon-success" />}
                          </span>
                          <span className="import-tab-name">{tabName}</span>
                          <span className="import-tab-badges">
                            {tabRes.inserted > 0 && <span className="badge-insert">{tabRes.inserted} inserted</span>}
                            {tabRes.updated > 0 && <span className="badge-update">{tabRes.updated} updated</span>}
                            {tabRes.skipped > 0 && <span className="badge-skip">{tabRes.skipped} skipped</span>}
                            {tabRes.failed > 0 && <span className="badge-fail">{tabRes.failed} failed</span>}
                          </span>
                          {hasFails && (
                            <span className="import-tab-expand">
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                          )}
                        </div>
                        {hasFails && isExpanded && (
                          <div className="import-error-details">
                            {tabRes.errors.map((err, idx) => (
                              <div key={idx} className="import-error-row">
                                <span className="error-op">{err.operation}</span>
                                <span className="error-key">{err.matchKey}</span>
                                <span className="error-msg">{err.error}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Final summary */}
                  <div className={`import-final-result ${importResult.totalFailed > 0 ? 'has-errors' : ''}`}>
                    <div className="import-final-summary">
                      {importResult.success ? (
                        <><CheckCircle size={20} className="icon-success" /> Import Complete</>
                      ) : (
                        <><AlertTriangle size={20} className="icon-warning" /> Import Complete with Errors</>
                      )}
                    </div>
                    <div className="import-final-counts">
                      {importResult.totalInserted > 0 && <span className="badge-insert">{importResult.totalInserted} inserted</span>}
                      {importResult.totalUpdated > 0 && <span className="badge-update">{importResult.totalUpdated} updated</span>}
                      {importResult.totalSkipped > 0 && <span className="badge-skip">{importResult.totalSkipped} skipped</span>}
                      {importResult.totalFailed > 0 && <span className="badge-fail">{importResult.totalFailed} failed</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ImportTemplatePage;
